import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import DeleteIcon from '@mui/icons-material/Delete'
import ErrorIcon from '@mui/icons-material/Error'
import ModeEditIcon from '@mui/icons-material/ModeEdit'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography
} from '@mui/material'
import {
  DataGridPro,
  GridActionsCellItem,
  GridAlignment,
  GridEditInputCell,
  GridPreProcessEditCellProps,
  GridRenderCellParams,
  GridRenderEditCellParams,
  GridRowModel,
  GridRowParams,
  GridRowsProp,
  GridValidRowModel,
  GridValueFormatterParams,
  useGridApiContext
} from '@mui/x-data-grid-pro'
import { DateTimePicker } from '@mui/x-date-pickers-pro'
import { Dayjs } from 'dayjs'
import {
  FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react'
import { v4 } from 'uuid'
import * as yup from 'yup'

import useAccessTokenAndShopId from '../../hooks/useAccessTokenAndShopId/useAccessTokenAndShopId'
import { FeatureFlags } from '../../models/FeatureFlags'
import {
  LabelsInventoryInstance,
  LabelsInventoryParts,
  LabelsPartsInfo
} from '../../models/Labels'
import {
  check2DecimalNumber,
  check2DecimalNumberNullable,
  checkIntQuantityPositive,
  checkName
} from '../../models/validation'
import { AppContext } from '../../providers/AppProvider/AppProvider'
import {
  useCreateInventoryInstanceMutation,
  useCreateInventoryPartMutation,
  useLazyGetInventoryPartsByPartNumberAndBrandQuery,
  useUpdateInventoryPartMutation
} from '../../redux/api'
import { useCreatePurchaseOrderMutation } from '../../redux/api/purchaseOrderApi'
import {
  InventoryPartInfo,
  InventoryPartInstanceInfo,
  InventoryPartInstanceInfoUnitOfMeasureEnum,
  VendorEntry
} from '../../sdk'
import { formatCurrency } from '../../utils/formatAmount/formatAmount'
import { processDataGridRowUpdate } from '../../utils/processDatagridRowUpdate/processDatagridRowUpdate'
import DocumentAnalysis, {
  ExpectedTableFillSuggestionRow
} from '../SharedUI/DocumentAnalysis'
import { LoadingBackdrop } from '../SharedUI/LoadingBackdrop'
import {
  BrandCreatableAutoComplete,
  ModifiedBrand
} from './useEditInventory/BrandCreatableAutoComplete'
import { SelectVendorAutocomplete } from './useEditInventoryInstance/SelectVendorAutocomplete'

interface Props {
  handleCancel: () => void
}

// possible states of each row
enum InvoiceSubmissionStateEnum {
  Editing = 'Editing',
  Success = 'Success',
  Error = 'Error'
}

const BrandEditCell: FC<GridRenderCellParams> = (
  params: GridRenderEditCellParams
) => {
  const { id, value, field, error } = params

  const apiContext = useGridApiContext()

  const [selectedBrand, setSelectedBrand] = useState<ModifiedBrand | undefined>(
    undefined
  )

  useEffect(() => {
    if (selectedBrand) {
      apiContext.current.setEditCellValue({
        id,
        field,
        value: selectedBrand
      })

      // close edit mode of the cell once selected
      apiContext.current.stopCellEditMode({ id, field })
    }
  }, [apiContext, field, id, selectedBrand, value])

  return (
    // set to fullWidth of cell
    <Tooltip open={!!error} title={error}>
      <div style={{ width: '100%' }}>
        <BrandCreatableAutoComplete
          {...{
            selectedBrand,
            setSelectedBrand,
            defaultValues: undefined, // new form so no past information
            clearable: false
          }}
        />
      </div>
    </Tooltip>
  )
}

const InvoiceStatusCell: FC<GridRenderCellParams> = (
  params: GridRenderCellParams
) => {
  // objective: have icon to show what is current state
  // objective: have tooltip to explain further
  switch (params.value) {
    case InvoiceSubmissionStateEnum.Editing:
      return (
        <Tooltip title={InvoiceSubmissionStateEnum.Editing} arrow>
          <ModeEditIcon />
        </Tooltip>
      )
    case InvoiceSubmissionStateEnum.Error:
      return (
        // hover over icon to show error message because message too long to display fully in cell
        <Tooltip title={params.row.errorMessage} arrow>
          <ErrorIcon />
        </Tooltip>
      )
    case InvoiceSubmissionStateEnum.Success:
      return (
        <Tooltip title={InvoiceSubmissionStateEnum.Success} arrow>
          <CheckIcon />
        </Tooltip>
      )
    default:
      return <>{params.value}</>
  }
}

const CellWithValidation: FC<GridRenderEditCellParams> = (
  params: GridRenderEditCellParams
) => {
  const { error } = params

  return (
    <Tooltip open={!!error} title={error}>
      <GridEditInputCell {...params} error={!!error} />
    </Tooltip>
  )
}

const UnitOfMeasureCell: FC<GridRenderEditCellParams> = (
  params: GridRenderEditCellParams
) => {
  const { id, value, field } = params

  const apiContext = useGridApiContext()

  return (
    <FormControl fullWidth>
      <InputLabel id="unit-of-measure-label-id">
        {LabelsInventoryParts.unitOfMeasure}
      </InputLabel>
      <Select
        labelId="unit-of-measure-label-id"
        id="unit-of-measure-select"
        value={value || InventoryPartInstanceInfoUnitOfMeasureEnum.DiscreteUnit}
        label={LabelsInventoryParts.unitOfMeasure}
        onChange={(e) => {
          apiContext.current.setEditCellValue({
            id,
            field,
            value: e.target.value
          })

          // close edit mode of the cell once selected
          apiContext.current.stopCellEditMode({ id, field })
        }}>
        {params.row.isFluids ? (
          Object.values(InventoryPartInstanceInfoUnitOfMeasureEnum).map(
            (unit) => (
              <MenuItem key={unit} value={unit}>
                {unit}
              </MenuItem>
            )
          )
        ) : (
          <MenuItem
            key={InventoryPartInstanceInfoUnitOfMeasureEnum.DiscreteUnit}
            value={InventoryPartInstanceInfoUnitOfMeasureEnum.DiscreteUnit}>
            {InventoryPartInstanceInfoUnitOfMeasureEnum.DiscreteUnit}
          </MenuItem>
        )}
      </Select>
    </FormControl>
  )
}

export const AddInvoiceDialog: FC<Props> = ({ handleCancel, ...props }) => {
  const { featureFlags } = useContext(AppContext)
  const { accessToken, shopId } = useAccessTokenAndShopId()
  const [createPurchaseOrder] = useCreatePurchaseOrderMutation()

  // separate invoiceNumber and vendor fields because not part of table
  const [invoiceNumber, setInvoiceNumber] = useState<string>('')
  const [vendor, setVendor] = useState<VendorEntry | undefined>(undefined)
  const [invoiceDate, setInvoiceDate] = useState<Dayjs | null>(null)
  const [subtotal, setSubtotal] = useState<string>('')
  const [tax, setTax] = useState<string>('')
  const [total, setTotal] = useState<string>('')

  const initialRows: GridRowsProp = [
    {
      id: v4(),
      status: InvoiceSubmissionStateEnum.Editing,
      name: '',
      partNumber: '',
      brand: '',
      cost: '',
      coreCost: '',
      isFluids: false,
      quantity: '',
      unitOfMeasure: InventoryPartInstanceInfoUnitOfMeasureEnum.DiscreteUnit,
      errorMessage: '',
      isFluidsEditable: true,
      autoAdded: false
    }
  ]

  // rows is always the most updated row state of the table
  const [rows, setRows] = useState<GridRowsProp>(initialRows)
  const [hasEmptyFields, setHasEmptyFields] = useState<boolean>(true)

  const checkFieldsEmpty = useCallback(
    (currentRows: readonly GridValidRowModel[]) => {
      let isEmpty = false

      let newRows: GridRowsProp = []

      for (const key in currentRows) {
        const data = currentRows[key]

        // if success previously then skip
        if (data.status === InvoiceSubmissionStateEnum.Success) {
          newRows = newRows.concat({ ...data })
          continue
        }

        // check if any field is empty
        const missingFields: string[] = []
        if (!invoiceNumber) {
          missingFields.push('invoiceNumber')
        }
        if (!vendor) {
          missingFields.push('vendor')
        }
        if (!invoiceDate) {
          missingFields.push('invoiceDate')
        }
        if (!subtotal) {
          missingFields.push('subtotal')
        }
        if (!tax) {
          missingFields.push('tax')
        }
        if (!total) {
          missingFields.push('total')
        }

        for (const [field, value] of Object.entries(data)) {
          if (
            // sometimes there is an undefined field, seems to be because of delete field
            // cell is set to undefined or '' based on default values
            // cell is set to null when no input
            field !== 'undefined' &&
            field !== 'errorMessage' &&
            field !== 'coreCost' && // coreCost can be null
            (value === undefined || value === null || value === '')
          ) {
            missingFields.push(field)
          }
        }
        if (missingFields.length === 0) {
          // valid non-empty rows remaining
          newRows = newRows.concat({
            ...data,
            status: InvoiceSubmissionStateEnum.Editing,
            errorMessage: ''
          })

          // rows are updated at the end because it is still looping through
        } else {
          // missingFields.length > 0
          const errorMessage = `ValidationError: Missing ${missingFields.join(
            ', '
          )} fields`
          newRows = newRows.concat({
            ...data,
            status: InvoiceSubmissionStateEnum.Error,
            errorMessage
          })

          isEmpty = true

          // rows are updated at the end because it is still looping through
        }
      }

      setRows(newRows)

      return isEmpty
    },
    [invoiceNumber, vendor, invoiceDate, subtotal, tax, total]
  )

  const handleRowAdd = useCallback(() => {
    const newRows = [...rows]
    newRows.push({
      id: v4(),
      status: InvoiceSubmissionStateEnum.Editing,
      name: '',
      partNumber: '',
      brand: '',
      cost: '',
      coreCost: '',
      isFluids: false,
      quantity: '',
      unitOfMeasure: InventoryPartInstanceInfoUnitOfMeasureEnum.DiscreteUnit,
      errorMessage: '',
      isFluidsEditable: true,
      autoAdded: false
    })
    checkFieldsEmpty(newRows)
  }, [checkFieldsEmpty, rows])

  const [triggerSearch] = useLazyGetInventoryPartsByPartNumberAndBrandQuery()

  const handleCellEdit = useCallback(
    async (newRow: GridRowModel, oldRow: GridRowModel) => {
      // get differing row
      const { id, field, value } = processDataGridRowUpdate(newRow, oldRow)

      // update differing row
      let editedRows: GridValidRowModel[] = rows.map((row) => {
        if (row.id === id) {
          return { ...row, [field]: value }
        }
        return row
      })

      // handle fluid restrictions
      if (
        (field === 'brand' || field === 'partNumber') &&
        newRow.brand &&
        newRow.partNumber
      ) {
        // handle existing inventory part fluid status
        const getInventoryPart = newRow.brand.brandName // handle custom brand
          ? await triggerSearch({
              authorization: accessToken,
              shop: shopId,
              brand: newRow.brand.brandName,
              part: newRow.partNumber
            }).unwrap()
          : undefined

        // existing inventory
        if (getInventoryPart && getInventoryPart.length === 1) {
          editedRows = editedRows.map((row) => {
            if (row.id === id) {
              return {
                ...row,
                isFluids: getInventoryPart[0].info?.isFluids,
                isFluidsEditable: false
              }
            }
            return row
          })
        } else {
          editedRows = editedRows.map((row) => {
            if (row.id === id) {
              return {
                ...row,
                isFluidsEditable: true
              }
            }
            return row
          })
        }
      } else if (field === 'isFluids') {
        // enforce discrete_unit restriction on non-fluids
        editedRows = editedRows.map((row) => {
          if (row.id === id) {
            return {
              ...row,
              unitOfMeasure: value
                ? newRow.unitOfMeasure
                : InventoryPartInstanceInfoUnitOfMeasureEnum.DiscreteUnit
            }
          }
          return row
        })
      }

      // check for empty rows and update table accordingly
      setHasEmptyFields(checkFieldsEmpty(editedRows))

      return newRow
    },
    [accessToken, checkFieldsEmpty, rows, shopId, triggerSearch]
  )

  // allow for confirmation of deleting row
  const [rowToDelete, setRowToDelete] = useState<string>('')

  const handleRowDelete = useCallback(() => {
    const newRows = rows.filter((row) => row.id !== rowToDelete)

    setHasEmptyFields(checkFieldsEmpty(newRows))

    setRowToDelete('')
  }, [checkFieldsEmpty, rowToDelete, rows])

  const [createInventoryPart] = useCreateInventoryPartMutation()
  const [updateInventoryPart] = useUpdateInventoryPartMutation()
  const [createInventoryInstance] = useCreateInventoryInstanceMutation()

  // loading state for checking when promises complete
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const handleSubmit = useCallback(async () => {
    setIsLoading(true)

    let isCompleted = true

    let newRows: GridRowsProp = []

    const purchaseOrderId = await createPurchaseOrder({
      authorization: accessToken,
      createPurchaseOrderRequest: {
        shop: shopId,
        info: {
          vendor: vendor!.id!,
          invoiceDate: invoiceDate!.toDate(),
          number: invoiceNumber!,
          subtotal: Number(subtotal),
          tax: Number(tax),
          total: Number(total)
        }
      }
    }).unwrap()

    // for each row
    for (const key in rows) {
      const data = rows[key]

      try {
        // if success previously then skip
        if (data.status === InvoiceSubmissionStateEnum.Success) {
          newRows = newRows.concat({ ...data })
          continue
        }

        // check whether in database
        // part number and brand is unique
        const getInventoryPart = data.brand.brandName // handle custom brand
          ? await triggerSearch({
              authorization: accessToken,
              shop: shopId,
              brand: data.brand.brandName,
              part: data.partNumber
            }).unwrap()
          : undefined

        let inventoryPartId = ''

        // existing inventory
        if (getInventoryPart && getInventoryPart.length === 1) {
          inventoryPartId = getInventoryPart[0].id!

          // if in database, then update new inventory item
          const info: InventoryPartInfo = {
            ...getInventoryPart[0].info,
            name: data.name,
            archived: undefined
          }

          await updateInventoryPart({
            authorization: accessToken,
            inventoryPartEntry: {
              id: inventoryPartId,
              info
            }
          })
        } else {
          // if not in database, create new inventory part, then create new inventory item
          const infoForNewPart: InventoryPartInfo = {
            shop: shopId,
            name: data.name,
            brand: data.brand.brandName || undefined,
            partNumber: data.partNumber,
            brandDisplayName:
              data.brand.inputValue || data.brand.displayName || undefined,
            isFluids: data.isFluids
          }

          inventoryPartId = await createInventoryPart({
            authorization: accessToken,
            inventoryPartInfo: infoForNewPart
          }).unwrap()
        }

        const infoForNewPartInstance: InventoryPartInstanceInfo = {
          inventoryPartId, // not validated above because of create/search
          invoiceNumber,
          supplierName: undefined,
          partsTechSupplierId: undefined,
          cost: data.cost,
          coreRefund: data.coreCost,
          quantity:
            data.unitOfMeasure ===
            InventoryPartInstanceInfoUnitOfMeasureEnum.DiscreteUnit
              ? 1
              : data.quantity,
          unitOfMeasure: data.unitOfMeasure,
          vendorId: vendor?.id,
          purchaseOrder: purchaseOrderId
        }

        const ordered = new Date()

        const createInventoryInstancePromises = []
        for (
          let i = 0;
          i <
          (data.unitOfMeasure ===
          InventoryPartInstanceInfoUnitOfMeasureEnum.DiscreteUnit
            ? data.quantity
            : 1);
          i++
        ) {
          const createInventoryInstancePromise = createInventoryInstance({
            authorization: accessToken,
            inventoryPartInstanceInfo: {
              ...infoForNewPartInstance,
              ordered: new Date(ordered.getTime() + i)
            }
          })

          createInventoryInstancePromises.push(createInventoryInstancePromise)
        }
        await Promise.all(createInventoryInstancePromises)

        // valid data
        newRows = newRows.concat({
          ...data,
          status: InvoiceSubmissionStateEnum.Success
        })
        // rows are updated at the end because it is still looping through
      } catch (error: any) {
        // invalid data becuase validation failed
        newRows = newRows.concat({
          ...data,
          status: InvoiceSubmissionStateEnum.Error,
          errorMessage: error.toString() // error needs to be of type any else cannot use .toString method
        })
        // rows are updated at the end because it is still looping through}

        isCompleted = false
      }
    }

    // update status of all rows
    setRows(newRows)

    // let user update fields with error if needed
    setHasEmptyFields(true)

    setIsLoading(false)

    if (isCompleted) {
      handleCancel()
    }
  }, [
    accessToken,
    createInventoryInstance,
    createInventoryPart,
    createPurchaseOrder,
    handleCancel,
    invoiceDate,
    invoiceNumber,
    rows,
    shopId,
    subtotal,
    tax,
    total,
    triggerSearch,
    updateInventoryPart,
    vendor
  ])

  const handleAccept: (id: string) => void = useCallback(
    (id) =>
      setRows((prevRows) => {
        const foundIndex = prevRows.findIndex((row) => row.id === id)
        if (foundIndex === -1) {
          return prevRows
        }
        const foundRowCopy = { ...prevRows[foundIndex], autoAdded: false }
        const newRows = prevRows.slice()
        newRows[foundIndex] = foundRowCopy
        return newRows
      }),
    []
  )

  const handleReject: (id: string) => void = useCallback(
    (id) =>
      setRows((prevRows) => {
        const newRows = prevRows.filter((row) => row.id !== id)
        setHasEmptyFields(checkFieldsEmpty(newRows))
        return newRows
      }),
    [checkFieldsEmpty]
  )

  const columns = useMemo(
    () => [
      {
        field: 'status',
        headerName: 'Status',
        headerAlign: 'center' as GridAlignment, // column show error if not cast to GridAlignment
        align: 'center' as GridAlignment, // column show error if not cast to GridAlignment
        flex: 1,
        editable: false,
        sortable: true,
        disableReorder: true, // set to left hand side of table
        renderCell: (params: GridRenderCellParams) => (
          <InvoiceStatusCell {...params} />
        )
      },
      {
        field: 'name',
        headerName: LabelsInventoryParts.name,
        headerAlign: 'center' as GridAlignment,
        align: 'center' as GridAlignment,
        flex: 1,
        editable: true,
        sortable: false,
        renderEditCell: (params: GridRenderEditCellParams) => {
          const { error } = params

          if (error) {
            setHasEmptyFields(true)
          }

          return <CellWithValidation {...params} />
        },
        preProcessEditCellProps: async (
          params: GridPreProcessEditCellProps
        ) => {
          let errorMessage = ''
          try {
            await yup
              .object()
              .shape({
                name: checkName('Name', 2).required()
              })
              .validate({
                name: params.props.value
              })
          } catch (error: any) {
            errorMessage = error.toString()
          }
          return { ...params.props, error: errorMessage }
        }
      },
      {
        field: 'partNumber',
        headerName: LabelsInventoryParts.partNumber,
        headerAlign: 'center' as GridAlignment,
        align: 'center' as GridAlignment,
        flex: 1,
        editable: true,
        sortable: false,
        renderEditCell: (params: GridRenderEditCellParams) => {
          return <CellWithValidation {...params} />
        },
        preProcessEditCellProps: async (
          params: GridPreProcessEditCellProps
        ) => {
          let errorMessage = ''
          try {
            await yup
              .object()
              .shape({
                partNumber: yup
                  .string()
                  .required()
                  .min(3, `Part Number should be at least 3 characters`)
              })
              .validate({
                partNumber: params.props.value
              })
          } catch (error: any) {
            errorMessage = error.toString()
          }
          return { ...params.props, error: errorMessage }
        }
      },
      {
        field: 'brand',
        headerName: LabelsInventoryParts.brand,
        headerAlign: 'center' as GridAlignment,
        align: 'center' as GridAlignment,
        flex: 1,
        editable: true,
        sortable: false,
        minWidth: 150, // minWidth such that select input does not look weird
        valueFormatter: (params: GridValueFormatterParams) => {
          return params.value.inputValue || params.value.displayName
            ? params.value.inputValue || params.value.displayName
            : ''
        },
        renderEditCell: (params: GridRenderEditCellParams) => (
          <BrandEditCell {...params} />
        ),
        preProcessEditCellProps: async (
          params: GridPreProcessEditCellProps
        ) => {
          let errorMessage = ''
          try {
            await yup
              .object()
              .shape({
                brand: yup.string().required()
              })
              .validate({
                brand:
                  params.props.value.inputValue ||
                  params.props.value.displayName
              })
          } catch (error: any) {
            errorMessage = error.toString()
          }
          return { ...params.props, error: errorMessage }
        }
      },
      {
        field: 'cost',
        headerName: LabelsPartsInfo.costPerUnit,
        headerAlign: 'center' as GridAlignment,
        align: 'center' as GridAlignment,
        flex: 1,
        editable: true,
        sortable: false,
        type: 'number',
        valueFormatter: (params: GridValueFormatterParams) => {
          return params.value || params.value === 0 // handle falsy 0 value
            ? formatCurrency(params.value, true)
            : ''
        },
        renderEditCell: (params: GridRenderEditCellParams) => {
          return <CellWithValidation {...params} />
        },
        preProcessEditCellProps: async (
          params: GridPreProcessEditCellProps
        ) => {
          let errorMessage = ''
          try {
            await yup
              .object()
              .shape({
                cost: check2DecimalNumber('Cost')
              })
              .validate({
                cost: params.props.value
              })
          } catch (error: any) {
            errorMessage = error.toString()
          }
          return { ...params.props, error: errorMessage }
        }
      },
      {
        field: 'coreCost',
        headerName: LabelsPartsInfo.corePerUnit,
        headerAlign: 'center' as GridAlignment,
        align: 'center' as GridAlignment,
        flex: 1,
        editable: true,
        sortable: false,
        type: 'number',
        valueFormatter: (params: GridValueFormatterParams) => {
          return params.value || params.value === 0 // handle falsy 0 value
            ? formatCurrency(params.value, true)
            : ''
        },
        renderEditCell: (params: GridRenderEditCellParams) => {
          return <CellWithValidation {...params} />
        },
        preProcessEditCellProps: async (
          params: GridPreProcessEditCellProps
        ) => {
          let errorMessage = ''
          try {
            await yup
              .object()
              .shape({
                coreCost: check2DecimalNumberNullable('Core cost')
              })
              .validate({
                coreCost: params.props.value
              })
          } catch (error: any) {
            errorMessage = error.toString()
          }
          return { ...params.props, error: errorMessage }
        }
      },
      {
        field: 'isFluids',
        headerName: LabelsInventoryParts.isFluids,
        headerAlign: 'center' as GridAlignment,
        align: 'center' as GridAlignment,
        flex: 1,
        editable: true,
        sortable: false,
        type: 'boolean'
      },
      {
        field: 'quantity',
        headerName: LabelsPartsInfo.quantity,
        headerAlign: 'center' as GridAlignment,
        align: 'center' as GridAlignment,
        flex: 1,
        editable: true,
        sortable: false,
        type: 'number',
        renderEditCell: (params: GridRenderEditCellParams) => {
          return <CellWithValidation {...params} />
        },
        preProcessEditCellProps: async (
          params: GridPreProcessEditCellProps
        ) => {
          let errorMessage = ''
          try {
            await yup
              .object()
              .shape({
                quantity: checkIntQuantityPositive('Quantity')
              })
              .validate({
                quantity: params.props.value
              })
          } catch (error: any) {
            errorMessage = error.toString()
          }
          return { ...params.props, error: errorMessage }
        }
      },
      {
        field: 'unitOfMeasure',
        headerName: LabelsInventoryInstance.unitOfMeasure,
        headerAlign: 'center' as GridAlignment,
        align: 'center' as GridAlignment,
        flex: 1,
        editable: true,
        sortable: false,
        minWidth: 140, // minWidth such that select input does not look weird
        renderEditCell: (params: GridRenderEditCellParams) => {
          return <UnitOfMeasureCell {...params} />
        }
      },
      {
        field: 'actions',
        headerName: 'Delete',
        headerAlign: 'center' as GridAlignment,
        align: 'center' as GridAlignment,
        flex: 1,
        type: 'actions',
        disableReorder: true, // set to right hand side of table
        getActions: (params: GridRowParams) =>
          params.row.autoAdded
            ? [
                <GridActionsCellItem
                  label="Accept"
                  key="Accept"
                  icon={
                    <Tooltip title="Accept">
                      <CheckIcon color="success" />
                    </Tooltip>
                  }
                  onClick={() => handleAccept(params.id as string)}
                />,
                <GridActionsCellItem
                  label="Reject"
                  key="Reject"
                  icon={
                    <Tooltip title="Reject">
                      <CloseIcon color="error" />
                    </Tooltip>
                  }
                  onClick={() => handleReject(params.id as string)}
                />
              ]
            : [
                <GridActionsCellItem
                  key={params.id}
                  icon={
                    <Tooltip title="Remove">
                      <DeleteIcon />
                    </Tooltip>
                  }
                  label="Remove"
                  onClick={() => setRowToDelete(params.id as string)}
                />
              ]
      }
    ],
    [handleAccept, handleReject]
  )

  const fillSuggestedRowsCallback: (
    rows: ExpectedTableFillSuggestionRow[],
    autoAdded: boolean
  ) => void = useCallback((rows, autoAdded) => {
    setRows((prevRows) =>
      prevRows.concat(
        rows.map((row) => ({
          id: v4(),
          status: InvoiceSubmissionStateEnum.Editing,
          name: row.Name,
          partNumber: row['Part Number'],
          brand: row.Brand,
          cost: row['Unit Cost'],
          coreCost: row['Unit Core Cost'],
          isFluids: false,
          quantity: row.Quantity,
          unitOfMeasure:
            InventoryPartInstanceInfoUnitOfMeasureEnum.DiscreteUnit,
          errorMessage: '',
          isFluidsEditable: true,
          autoAdded
        }))
      )
    )
  }, [])

  return (
    <Dialog {...props} open={true} maxWidth="lg" fullWidth fullScreen>
      {isLoading && <LoadingBackdrop />}
      <Stack direction="row" sx={{ justifyContent: 'space-between' }}>
        <DialogTitle variant="h2">New Invoice</DialogTitle>
        <DialogActions sx={{ pt: 2 }}>
          <Button
            onClick={() => {
              handleCancel()
            }}>
            Close
          </Button>
          <Button
            onClick={() => {
              handleSubmit()
            }}
            variant="contained"
            disabled={hasEmptyFields || rows.length === 0}>
            Submit
          </Button>
        </DialogActions>
      </Stack>
      <DialogContent sx={{ pb: 0 }}>
        <Dialog open={!!rowToDelete} onClose={() => setRowToDelete('')}>
          <DialogTitle pb={0}>Delete Row?</DialogTitle>
          <DialogActions sx={{ spacing: 2 }}>
            <Button onClick={() => setRowToDelete('')}>Cancel</Button>
            <Button variant="contained" onClick={handleRowDelete}>
              Confirm
            </Button>
          </DialogActions>
        </Dialog>
        <Stack spacing={1}>
          <Box>
            <Typography>Fill in all fields to submit</Typography>
            <Typography>
              Use the &rsquo;New Row&rsquo; button to add more items
            </Typography>
          </Box>
          <Stack
            direction="row"
            justifyContent="space-between"
            sx={{ pt: 1, pb: 1 }}>
            <Stack direction="row" spacing={2}>
              <TextField
                name="invoiceNumber"
                label="Invoice Number"
                value={invoiceNumber}
                onChange={(e) => setInvoiceNumber(e.target.value)}
                type="text"
                variant="standard"
                sx={{ minWidth: '200px' }}
                onBlur={() => {
                  setHasEmptyFields(checkFieldsEmpty(rows))
                }}
              />
              <Box sx={{ transform: 'translateX(-14px)' }}>
                <SelectVendorAutocomplete
                  vendor={vendor}
                  setVendor={setVendor}
                  onBlur={() => {
                    setHasEmptyFields(checkFieldsEmpty(rows))
                  }}
                  variant="standard"
                  sx={{ width: '100%', marginLeft: '14px' }}
                />
              </Box>
              <DateTimePicker
                label="Invoice Date"
                value={invoiceDate}
                onChange={(date) => setInvoiceDate(date)}
                slotProps={{ textField: { variant: 'standard' } }}
              />
              <TextField
                name="subtotal"
                label="Subtotal"
                type="number"
                variant="standard"
                onChange={(event) => setSubtotal(event.target.value)}
                onBlur={() => {
                  setHasEmptyFields(checkFieldsEmpty(rows))
                }}
              />
              <TextField
                name="tax"
                label="Tax"
                type="number"
                variant="standard"
                onChange={(event) => setTax(event.target.value)}
                onBlur={() => {
                  setHasEmptyFields(checkFieldsEmpty(rows))
                }}
              />
              <TextField
                name="total"
                label="Total"
                type="number"
                variant="standard"
                onChange={(event) => setTotal(event.target.value)}
                onBlur={() => {
                  setHasEmptyFields(checkFieldsEmpty(rows))
                }}
              />
            </Stack>
            <Button
              variant="outlined"
              size="small"
              startIcon={<AddCircleOutlineIcon />}
              onClick={handleRowAdd}
              sx={{ alignSelf: 'flex-end', height: '30px' }}>
              New Row
            </Button>
          </Stack>
          <DataGridPro
            columns={columns}
            rows={rows}
            processRowUpdate={handleCellEdit}
            autoHeight
            disableColumnMenu={true}
            hideFooter
            isCellEditable={(params) => {
              if (params.field === 'isFluids' && !params.row.isFluidsEditable) {
                return false
              }
              if (params.field === 'unitOfMeasure') {
                return !!params.row.isFluids
              }
              return params.row.status !== InvoiceSubmissionStateEnum.Success // ensure that unable to edit past submissions within dialog
            }}
          />
          {featureFlags && featureFlags[FeatureFlags.Textract] && (
            <DocumentAnalysis
              fillSuggestedRowsCallback={fillSuggestedRowsCallback}
            />
          )}
        </Stack>
      </DialogContent>
    </Dialog>
  )
}
